import React, { useEffect, useState, Component } from 'react';
import { Users, Building2, Calendar, TrendingUp, CheckCircle, XCircle, Eye, Edit, Trash2, Download, RefreshCw } from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/Button';
import { Brand, Organizer } from '../../services/dataService';
interface User {
  id: string;
  email: string;
  role: 'brand' | 'organizer';
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}
interface SystemStats {
  totalUsers: number;
  totalBrands: number;
  totalOrganizers: number;
  pendingApplications: number;
  activeMatches: number;
  totalRevenue: number;
}
export function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'applications' | 'analytics'>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalBrands: 0,
    totalOrganizers: 0,
    pendingApplications: 0,
    activeMatches: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadDashboardData();
  }, []);
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all users from localStorage
      const allBrands = JSON.parse(localStorage.getItem('brands') || '[]') as Brand[];
      const allOrganizers = JSON.parse(localStorage.getItem('organizers') || '[]') as Organizer[];
      // Combine into users array
      const brandUsers: User[] = allBrands.map(b => ({
        id: b.id,
        email: b.email,
        role: 'brand' as const,
        name: b.companyName,
        status: 'approved' as const,
        createdAt: b.createdAt.toString()
      }));
      const organizerUsers: User[] = allOrganizers.map(o => ({
        id: o.id,
        email: o.email,
        role: 'organizer' as const,
        name: o.organizerName,
        status: 'approved' as const,
        createdAt: o.createdAt.toString()
      }));
      const allUsers = [...brandUsers, ...organizerUsers];
      setUsers(allUsers);
      // Calculate stats
      setStats({
        totalUsers: allUsers.length,
        totalBrands: brandUsers.length,
        totalOrganizers: organizerUsers.length,
        pendingApplications: allUsers.filter(u => u.status === 'pending').length,
        activeMatches: Math.floor(Math.min(brandUsers.length, organizerUsers.length) * 0.7),
        totalRevenue: Math.floor(Math.random() * 500000) + 100000
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleApproveUser = async (userId: string) => {
    // In a real app, this would call an API
    setUsers(users.map(u => u.id === userId ? {
      ...u,
      status: 'approved' as const
    } : u));
  };
  const handleRejectUser = async (userId: string) => {
    // In a real app, this would call an API
    setUsers(users.map(u => u.id === userId ? {
      ...u,
      status: 'rejected' as const
    } : u));
  };
  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== userId));
    }
  };
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: TrendingUp
  }, {
    id: 'users',
    label: 'User Management',
    icon: Users
  }, {
    id: 'applications',
    label: 'Applications',
    icon: CheckCircle
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: TrendingUp
  }];
  return <DashboardLayout data-id="element-1300">
      <div className="space-y-6" data-id="element-1301">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1302">
          <div data-id="element-1303">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-1304">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1305">
              Complete platform control and oversight
            </p>
          </div>
          <div className="flex gap-3" data-id="element-1306">
            <Button onClick={loadDashboardData} variant="secondary" data-id="element-1307">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-1308" />
              Refresh Data
            </Button>
            <Button data-id="element-1309">
              <Download className="w-4 h-4 mr-2" data-id="element-1310" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-1311">
          <nav className="flex space-x-8" data-id="element-1312">
            {tabs.map(tab => {
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `} data-id="element-1313">
                  <Icon className="w-5 h-5 mr-2" data-id="element-1314" />
                  {tab.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-1315">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1316">
              <StatCard title="Total Users" value={stats.totalUsers} icon={Users} color="blue" trend="+12% from last month" data-id="element-1317" />
              <StatCard title="Total Brands" value={stats.totalBrands} icon={Building2} color="purple" trend="+8% from last month" data-id="element-1318" />
              <StatCard title="Total Organizers" value={stats.totalOrganizers} icon={Calendar} color="green" trend="+15% from last month" data-id="element-1319" />
              <StatCard title="Pending Applications" value={stats.pendingApplications} icon={CheckCircle} color="yellow" data-id="element-1320" />
              <StatCard title="Active Matches" value={stats.activeMatches} icon={TrendingUp} color="indigo" trend="+23% from last month" data-id="element-1321" />
              <StatCard title="Platform Revenue" value={`$${stats.totalRevenue.toLocaleString()}`} icon={TrendingUp} color="green" trend="+18% from last month" data-id="element-1322" />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6" data-id="element-1323">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1324">
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1325">
                <Button variant="secondary" className="justify-center" data-id="element-1326">
                  Review Applications
                </Button>
                <Button variant="secondary" className="justify-center" data-id="element-1327">
                  Manage Users
                </Button>
                <Button variant="secondary" className="justify-center" data-id="element-1328">
                  View Analytics
                </Button>
                <Button variant="secondary" className="justify-center" data-id="element-1329">
                  System Settings
                </Button>
              </div>
            </div>
          </div>}

        {/* Users Tab */}
        {activeTab === 'users' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1330">
            <div className="p-6 border-b border-gray-200" data-id="element-1331">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1332">All Users</h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1333">
                Manage all platform users and their access
              </p>
            </div>

            {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1334">
                Loading users...
              </div> : <div className="overflow-x-auto" data-id="element-1335">
                <table className="w-full" data-id="element-1336">
                  <thead className="bg-gray-50" data-id="element-1337">
                    <tr data-id="element-1338">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1339">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1340">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1341">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1342">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1343">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1344">
                    {users.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-1345">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1346">
                          <div data-id="element-1347">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1348">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1349">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1350">
                          <span className={`
                            px-2 py-1 text-xs font-medium rounded-full
                            ${user.role === 'brand' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}
                          `} data-id="element-1351">
                            {user.role === 'brand' ? 'Brand' : 'Organizer'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1352">
                          <span className={`
                            px-2 py-1 text-xs font-medium rounded-full
                            ${user.status === 'approved' ? 'bg-green-100 text-green-800' : user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}
                          `} data-id="element-1353">
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1354">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1355">
                          <div className="flex justify-end gap-2" data-id="element-1356">
                            {user.status === 'pending' && <>
                                <button onClick={() => handleApproveUser(user.id)} className="text-green-600 hover:text-green-900" title="Approve" data-id="element-1357">
                                  <CheckCircle className="w-5 h-5" data-id="element-1358" />
                                </button>
                                <button onClick={() => handleRejectUser(user.id)} className="text-red-600 hover:text-red-900" title="Reject" data-id="element-1359">
                                  <XCircle className="w-5 h-5" data-id="element-1360" />
                                </button>
                              </>}
                            <button className="text-blue-600 hover:text-blue-900" title="View" data-id="element-1361">
                              <Eye className="w-5 h-5" data-id="element-1362" />
                            </button>
                            <button className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-1363">
                              <Edit className="w-5 h-5" data-id="element-1364" />
                            </button>
                            <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-1365">
                              <Trash2 className="w-5 h-5" data-id="element-1366" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {/* Applications Tab */}
        {activeTab === 'applications' && <div className="bg-white rounded-lg shadow p-6" data-id="element-1367">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1368">
              Pending Applications
            </h2>
            <p className="text-gray-600" data-id="element-1369">
              {stats.pendingApplications} applications awaiting review
            </p>
            {/* Application review interface would go here */}
          </div>}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <div className="space-y-6" data-id="element-1370">
            <div className="bg-white rounded-lg shadow p-6" data-id="element-1371">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1372">
                Platform Analytics
              </h2>
              <p className="text-gray-600" data-id="element-1373">
                Comprehensive analytics and insights coming soon...
              </p>
            </div>
          </div>}
      </div>
    </DashboardLayout>;
}
interface StatCardProps {
  title: string;
  value: string | number;
  icon: ComponentType<{
    className?: string;
  }>;
  color: 'blue' | 'purple' | 'green' | 'yellow' | 'indigo';
  trend?: string;
}
function StatCard({
  title,
  value,
  icon: Icon,
  color,
  trend
}: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  };
  return <div className="bg-white rounded-lg shadow p-6" data-id="element-1374">
      <div className="flex items-center justify-between mb-4" data-id="element-1375">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`} data-id="element-1376">
          <Icon className="w-6 h-6" data-id="element-1377" />
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1" data-id="element-1378">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1379">{value}</p>
      {trend && <p className="text-sm text-green-600" data-id="element-1380">{trend}</p>}
    </div>;
}